import React, { createContext, useContext, useState, useCallback } from "react";

export interface Asset {
  symbol: string;
  name: string;
  balance: number;
  locked: number;
  priceUsd: number;
  change24h: number;
  icon: string;
}

export interface Transaction {
  id: string;
  type: "deposit" | "withdraw" | "trade" | "transfer";
  asset: string;
  amount: number;
  status: "completed" | "pending" | "failed";
  timestamp: Date;
  txHash?: string;
}

interface WalletContextType {
  assets: Asset[];
  transactions: Transaction[];
  totalBalanceUsd: number;
  totalChange24h: number;
  selectedPair: string;
  setSelectedPair: (pair: string) => void;
  refreshBalances: () => void;
}

const defaultAssets: Asset[] = [
  {
    symbol: "BTC",
    name: "Bitcoin",
    balance: 0.5234,
    locked: 0.1,
    priceUsd: 67250.0,
    change24h: 2.34,
    icon: "₿",
  },
  {
    symbol: "ETH",
    name: "Ethereum",
    balance: 8.4567,
    locked: 0.5,
    priceUsd: 3456.78,
    change24h: -1.23,
    icon: "Ξ",
  },
  {
    symbol: "USDT",
    name: "Tether",
    balance: 15420.5,
    locked: 2000,
    priceUsd: 1.0,
    change24h: 0.01,
    icon: "₮",
  },
  {
    symbol: "NGNT",
    name: "Nigeria Token",
    balance: 2500000,
    locked: 0,
    priceUsd: 0.00065,
    change24h: 0.85,
    icon: "🇳🇬",
  },
  {
    symbol: "GHST",
    name: "Ghana Token",
    balance: 180000,
    locked: 0,
    priceUsd: 0.0082,
    change24h: -0.45,
    icon: "🇬🇭",
  },
  {
    symbol: "KENT",
    name: "Kenya Token",
    balance: 950000,
    locked: 50000,
    priceUsd: 0.0071,
    change24h: 1.23,
    icon: "🇰🇪",
  },
  {
    symbol: "ZART",
    name: "South Africa Token",
    balance: 75000,
    locked: 0,
    priceUsd: 0.055,
    change24h: -0.78,
    icon: "🇿🇦",
  },
  {
    symbol: "EGPT",
    name: "Egypt Token",
    balance: 420000,
    locked: 0,
    priceUsd: 0.02,
    change24h: 0.34,
    icon: "🇪🇬",
  },
];

const defaultTransactions: Transaction[] = [
  {
    id: "tx1",
    type: "deposit",
    asset: "USDT",
    amount: 5000,
    status: "completed",
    timestamp: new Date(Date.now() - 86400000),
  },
  {
    id: "tx2",
    type: "trade",
    asset: "BTC",
    amount: 0.05,
    status: "completed",
    timestamp: new Date(Date.now() - 172800000),
  },
  {
    id: "tx3",
    type: "withdraw",
    asset: "NGNT",
    amount: 100000,
    status: "completed",
    timestamp: new Date(Date.now() - 259200000),
  },
  {
    id: "tx4",
    type: "transfer",
    asset: "GHST",
    amount: 5000,
    status: "pending",
    timestamp: new Date(Date.now() - 3600000),
  },
];

const WalletContext = createContext<WalletContextType | undefined>(undefined);

export function WalletProvider({ children }: { children: React.ReactNode }) {
  const [assets] = useState<Asset[]>(defaultAssets);
  const [transactions] = useState<Transaction[]>(defaultTransactions);
  const [selectedPair, setSelectedPair] = useState("BTC/USDT");

  const totalBalanceUsd = assets.reduce((sum, asset) => {
    return sum + (asset.balance + asset.locked) * asset.priceUsd;
  }, 0);

  const totalChange24h = assets.reduce((sum, asset) => {
    const value = (asset.balance + asset.locked) * asset.priceUsd;
    return sum + (value * asset.change24h) / 100;
  }, 0);

  const refreshBalances = useCallback(() => {
    // In a real app, this would fetch from an API
    console.log("Refreshing balances...");
  }, []);

  return (
    <WalletContext.Provider
      value={{
        assets,
        transactions,
        totalBalanceUsd,
        totalChange24h,
        selectedPair,
        setSelectedPair,
        refreshBalances,
      }}
    >
      {children}
    </WalletContext.Provider>
  );
}

export function useWallet() {
  const context = useContext(WalletContext);
  if (context === undefined) {
    throw new Error("useWallet must be used within a WalletProvider");
  }
  return context;
}
